import { format, isToday } from 'date-fns';

import {
  TrackingDelay,
  TrackingDetails,
  TrackingLocation,
  TrackingStyle,
  TrackingTimeWindow,
  TrackingWeight,
} from 'order/wizard/orderTracking/interface/OrderTracking';
import { TrackingStatusEnum } from 'order/wizard/orderTracking/enums/TrackingStatusEnum';
import UtilService from 'shared/services/util.service';

const fedExTrackingUrl = 'https://www.fedex.com/fedextrack/?trknbr=';

// FedEx status codes that aren't shown as "In transit".
const statusByFedExCode: Record<string, TrackingStatusEnum> = {
  OC: TrackingStatusEnum.LABEL_CREATED,
  OD: TrackingStatusEnum.OUT_FOR_DELIVERY,
  DL: TrackingStatusEnum.DELIVERED,
  DE: TrackingStatusEnum.EXCEPTION,
  SE: TrackingStatusEnum.EXCEPTION,
  CA: TrackingStatusEnum.CANCELLED,
};

export const getFedExTrackingUrl = (trackingNumber: string) =>
  `${fedExTrackingUrl}${encodeURIComponent(trackingNumber)}`;

// Groups digits by four for readability, e.g. "7749 9876 1204".
export const formatTrackingNumber = (trackingNumber: string) =>
  trackingNumber.replace(/(.{4})(?=.)/g, '$1 ');

export const isDelayed = (
  delay: TrackingDelay | null | undefined
): delay is TrackingDelay => delay?.status?.toUpperCase() === 'DELAYED';

// The delay of a box still on its way, null once delivered or when on time.
export const getTrackingDelay = (details: TrackingDetails) => {
  const delay = details.status?.delay;

  return !details.isDelivered && isDelayed(delay) ? delay : null;
};

export const getTrackingStatus = (details: TrackingDetails) => {
  if (details.isDelivered) return TrackingStatusEnum.DELIVERED;
  if (details.error) return TrackingStatusEnum.UNAVAILABLE;

  return (
    statusByFedExCode[details.status?.code?.toUpperCase() ?? ''] ??
    TrackingStatusEnum.IN_TRANSIT
  );
};

// A delay outranks the transit status, so a delayed box shows a single "Delayed" status in the list.
export const getTrackingListStatus = (details: TrackingDetails) =>
  getTrackingDelay(details)
    ? TrackingStatusEnum.DELAYED
    : getTrackingStatus(details);

export const getDelayLabel = (delay: TrackingDelay) =>
  delay.type
    ? `${TrackingStatusEnum.DELAYED} · ${UtilService.toCapitalize(delay.type)}`
    : TrackingStatusEnum.DELAYED;

// BE dates are UTC, but not every one of them carries the zone designator.
const toDate = (utc: string) =>
  new Date(/(Z|[+-]\d{2}:\d{2})$/.test(utc) ? utc : `${utc}Z`);

export const formatTrackingDate = (
  utc: string | null | undefined,
  dateFormat: string
) => (utc ? format(toDate(utc), dateFormat) : null);

export const formatTrackingDay = (utc: string | null | undefined) => {
  if (!utc) return null;

  const date = toDate(utc);

  return format(
    date,
    isToday(date) ? "'Today', MMM d, yyyy" : 'EEE, MMM d, yyyy'
  );
};

const formatTime = (date: Date) =>
  format(date, date.getMinutes() ? 'h:mm a' : 'h a');

// e.g. "12–4 PM" or "8 AM–6 PM", null unless both ends are known.
export const formatTimeWindow = (window: TrackingTimeWindow | null) => {
  if (!window?.beginsOnUtc || !window.endsOnUtc) return null;

  const begins = toDate(window.beginsOnUtc);
  const ends = toDate(window.endsOnUtc);

  const beginsTime =
    format(begins, 'a') === format(ends, 'a')
      ? formatTime(begins).replace(/ [AP]M$/, '')
      : formatTime(begins);

  return `${beginsTime}–${formatTime(ends)}`;
};

// FedEx sends cities in upper case, e.g. "FORT WAYNE".
export const formatLocation = (location: TrackingLocation | null) => {
  if (!location) return null;

  const city = location.city
    ?.toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  return (
    [city, location.stateOrProvinceCode].filter(Boolean).join(', ') || null
  );
};

export const formatWeight = (weight: TrackingWeight | null) =>
  weight ? `${weight.value} ${weight.unit?.toLowerCase() ?? ''}`.trim() : null;

export const getTotalWeight = (
  trackingDetails: TrackingDetails[]
): TrackingWeight | null => {
  const weights = trackingDetails
    .map((details) => details.weight)
    .filter((weight): weight is TrackingWeight => !!weight);

  if (!weights.length) return null;

  return {
    value: UtilService.roundToTwoDecimals(
      weights.reduce((total, weight) => total + weight.value, 0)
    ) as number,
    unit: weights[0].unit,
  };
};

export const formatStyleDetails = (style: TrackingStyle) =>
  [style.productLine, style.wood, style.finish, style.door]
    .filter(Boolean)
    .join(' · ');
